// Exact-to-artifact Manual values, not guessed variant/profile authority. The
// existing scope tokenizer owns HTML/conditional interpretation. Every editable
// span is inside the block and is rechecked against current bytes on each draft.
import { tokenize } from './html-source-tokens.js';
import { backgroundValue, styleDeclarations } from './background-fields.js';

const NUMBER = new Set(['width', 'height', 'max-width', 'min-width', 'font-size', 'line-height', 'letter-spacing', 'border-radius']);
const COLOR = new Set(['color', 'background-color', 'background', 'border-color']);
const SPACING = new Set(['padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left', 'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left']);
const OPTIONS = { 'text-align': ['left', 'center', 'right', 'justify'], 'vertical-align': ['top', 'middle', 'bottom', 'baseline'], 'font-weight': ['normal', 'bold', '100', '200', '300', '400', '500', '600', '700', '800', '900'], 'text-decoration': ['none', 'underline', 'line-through'] };
const INERT = new Set(['style', 'script', 'template', 'datalist', 'head', 'xml', 'svg', 'math', 'noscript', 'textarea']);
const LEAF = new Set(['td', 'th', 'p', 'div', 'span', 'a', 'b', 'strong', 'em', 'i', 'h1', 'h2', 'h3', 'li', 'font']);
const protectedValue = s => /\{\{|\{%|<%|%%/.test(s);
const numericLimit = (property, unit) => unit === '%'
  ? ['font-size', 'line-height'].includes(property) ? 1000 : 100
  : property === 'height' ? 100000 : property === 'line-height' && !unit ? 20 : 2000;
const label = s => s.replace(/-/g, ' ').replace(/^./, c => c.toUpperCase());
function decoded(raw) {
  if (/&(?!(?:amp|quot|apos|lt|gt|nbsp|#\d+|#x[\da-f]+);)/i.test(raw)) return null;
  return raw.replace(/&(#x[\da-f]+|#\d+|amp|quot|apos|lt|gt|nbsp);/gi, (_, key) => {
    if (key[0] !== '#') return { amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', nbsp: '\u00a0' }[key.toLowerCase()];
    const n = key[1].toLowerCase() === 'x' ? parseInt(key.slice(2), 16) : Number(key.slice(1));
    return n > 0 && n <= 0x10ffff && !(n >= 0xd800 && n <= 0xdfff) ? String.fromCodePoint(n) : '\ufffd';
  });
}
function attributeSpans(token) {
  const out = [], rx = /\s+([^\s=<>/"']+)\s*=\s*("[^"]*"|'[^']*')/g;
  for (const m of token.raw.matchAll(rx)) {
    const start = token.start + m.index + m[0].lastIndexOf(m[2]) + 1;
    out.push({ name: m[1].toLowerCase(), start, end: start + m[2].length - 2, raw: m[2].slice(1, -1) });
  }
  return out;
}
export function projectArtifactFields(code, { contentDigest = null, editable = true, source = 'artifact_html', includeEditControls = false } = {}) {
  const fields = {};
  let tokens;
  try { tokens = tokenize(code); } catch { return fields; }
  let conditional = 0, inert = 0, node = 0;
  const add = (key, title, binding, value, extra = {}) => {
    if (value == null) return;
    fields[key] = { schema: 'ArtifactField.v1', k: key, l: title, t: 'text', value, certainty: 'exact',
      editable: editable && !protectedValue(String(value)),
      provenance: { source, content_digest: contentDigest, authority: 'artifact_value_not_variant_rule' },
      binding, ...extra };
  };
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.kind === 'if') { conditional++; continue; }
    if (t.kind === 'endif') { conditional = Math.max(0, conditional - 1); continue; }
    if (conditional || t.kind !== 'tag') continue;
    if (t.close) { if (INERT.has(t.name)) inert = Math.max(0, inert - 1); continue; }
    if (INERT.has(t.name) && !t.void) inert++;
    if (inert || INERT.has(t.name)) continue;
    const prefix = `node_${node++}`, title = `${t.name.toUpperCase()} ${node}`;
    const attrs = attributeSpans(t);
    const counts = new Map(); for (const a of t.attrs) counts.set(a.name, (counts.get(a.name) || 0) + 1);
    // A background editor includes CSS defaults and can insert a declaration.
    // It is a draft capability, never an observed value in the artifact receipt.
    if(includeEditControls&&node===1&&['table','tr','td','div','body'].includes(t.name)&&!t.attrs.some(a=>a.name==='background')) {
      const style=attrs.find(a=>a.name==='style'), base=style?.raw||'';
      const value=backgroundValue(base);
      if(value!==null&&(!counts.has('style')||counts.get('style')===1&&style)) {
        const binding=style||{start:t.start+1+t.name.length,end:t.start+1+t.name.length,raw:''};
        add(`${prefix}_background_editor`,`${title} · Background image`,binding,value,{
          t:'background',base_style:base,style_mode:style?'value':'insert',certainty:'edit_controls',
          provenance:{source:'manual_edit_controls',content_digest:null,authority:'draft_capability_not_artifact_value'},
        });
      }
    }
    for (const a of attrs) {
      if (counts.get(a.name) !== 1) continue;
      if (a.name === 'alt' || a.name === 'title' || (a.name === 'href' && t.name === 'a')) {
        add(`${prefix}_${a.name}`, `${title} · ${label(a.name)}`, a, decoded(a.raw), { validation: a.name === 'href' ? 'url' : null });
      } else if (a.name === 'src' && t.name === 'img' && /^(?:\.\/)?images\/[A-Za-z0-9][A-Za-z0-9._-]{0,119}$/.test(a.raw)) {
        add(`${prefix}_src`, `${title} · Image source`, a, a.raw, { validation: 'local-asset' });
      } else if (['width', 'height'].includes(a.name) && /^\d+(?:\.\d+)?%?$/.test(a.raw)) {
        // CSS and HTML dimensions may differ intentionally for responsive/MSO
        // behavior; preserve them as separate explicitly-labelled values.
        add(`${prefix}_${a.name}_attribute`, `${title} · ${label(a.name)} attribute`, a, parseFloat(a.raw), { t: 'num', u: a.raw.endsWith('%') ? '%' : '', min: 0, max: numericLimit(a.name, a.raw.endsWith('%') ? '%' : '') });
      } else if (a.name === 'bgcolor' && /^#(?:[\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i.test(a.raw)) {
        add(`${prefix}_bgcolor`, `${title} · Background attribute`, a, a.raw, { t: 'color' });
      } else if (a.name === 'style') {
        const declarations = styleDeclarations(a.raw)||[];
        const duplicates = declarations.map(d => d.property);
        for (const d of declarations) {
          const property = d.property, raw = d.value;
          if (!raw || duplicates.filter(p => p === property).length !== 1 || /!important/i.test(raw)) continue;
          const offset = d.valueStart;
          const binding = { start: a.start + offset, end: a.start + offset + raw.length, raw };
          const key = `${prefix}_${property}`, name = `${title} · ${label(property)}`;
          if (NUMBER.has(property) && /^\d+(?:\.\d+)?(?:px|%)?$/.test(raw)
              && (/(?:px|%)$/.test(raw) || Number(raw) === 0 || property === 'line-height')) {
            // Bare zero is valid CSS, but a nonzero edited length needs a unit.
            // Unitless line-height is a multiplier and must remain unitless.
            const u = raw.endsWith('%') ? '%' : raw.endsWith('px') || property !== 'line-height' ? 'px' : '';
            add(key, name, binding, parseFloat(raw), { t: 'num', u, min: 0, max: numericLimit(property, u) });
          }
          else if (COLOR.has(property) && /^#(?:[\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i.test(raw)) add(key, name, binding, raw, { t: 'color' });
          else if (SPACING.has(property) && /^(?:0|\d+(?:\.\d+)?(?:px|%))(?:\s+(?:0|\d+(?:\.\d+)?(?:px|%))){0,3}$/.test(raw)) add(key, name, binding, raw, { validation: 'spacing' });
          else if (OPTIONS[property]?.includes(raw)) add(key, name, binding, raw, { options: OPTIONS[property] });
          else if (property === 'font-family' && /^[\w ,\-]+$/.test(raw)) add(key, name, binding, raw, { validation: 'font-family' });
        }
      }
    }
    const text = tokens[i + 1], close = tokens[i + 2];
    if (LEAF.has(t.name) && text?.kind === 'text' && close?.kind === 'tag' && close.close && close.name === t.name && text.raw.trim()) {
      const raw = text.raw.trim(), start = text.start + text.raw.indexOf(raw);
      const value = decoded(raw);
      if (value?.trim()) add(`${prefix}_copy`, `${title} · Text`, { start, end: start + raw.length, raw }, value, { t: 'ta' });
    }
  }
  return fields;
}

// Use only for authored, Manual-eligible regions. The server reconstructs these
// capabilities from the current source when proving an amendment; client field
// schemas and defaults do not grant write authority.
export function projectManualFields(code, options = {}) {
  if (options.engine === 'codex') {
    const kinds = { copy: 'content.copy', href: 'content.href', alt: 'content.alt_text', src: 'content.asset_id' };
    const fields = projectArtifactFields(code, options), out = {}, encoder = new TextEncoder();
    for (const [key, field] of Object.entries(fields)) {
      const kind = kinds[key.split('_').at(-1)];
      if (!kind) continue;
      const { start, end, raw } = field.binding;
      // Keep the existing string setter binding and expose its exact byte span.
      out[key] = { ...field, kind, recipe: null,
        byte_binding: { start: encoder.encode(code.slice(0, start)).length,
          end: encoder.encode(code.slice(0, end)).length, raw } };
    }
    return out;
  }
  return projectArtifactFields(code, { ...options, includeEditControls: true });
}
